import type { ReadNotificationsType } from '@repo/types/notifications';

import { NotificationKey } from '@repo/api/notification/keys';
import { usePostReadAllNotifications } from '@repo/api/notification/usePostReadAllNotifications';
import { usePostReadNotifications } from '@repo/api/notification/usePostReadNotification';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { NotificationStatus } from '@repo/types/enums/notificationStatus';
import { useQueryClient } from '@tanstack/react-query';

import NotificationsSheetActions from '@/components/features/notifications/NotificationSheetActions';
import { useNotificationsSheet } from '@/components/features/notifications/NotificationSheetProvider';
import NotificationsList from '@/components/features/notifications/NotificationsList';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle';
import { useLocalStorage } from '@/hooks/local-storage';
import { useGetNotificationsInfinite } from '@/hooks/notifications';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

const NOTIFICATION_STATUS_FILTER_KEY = 'notifications-status-filter';

function isNotificationStatus(value: string): value is NotificationStatus {
  return Object.values(NotificationStatus).some((notificationStatus) => notificationStatus === value);
}

function deserializeNotificationStatus(value: string): NotificationStatus {
  const parsed: unknown = JSON.parse(value);
  return typeof parsed === 'string' && isNotificationStatus(parsed) ? parsed : NotificationStatus.All;
}

export default function NotificationsSheet() {
  const queryClient = useQueryClient();
  const { open, onOpenChange } = useNotificationsSheet();
  const [statusFilter, setStatusFilter] = useLocalStorage<NotificationStatus>(NOTIFICATION_STATUS_FILTER_KEY, NotificationStatus.All, { deserializer: deserializeNotificationStatus });
  const { data, fetchNextPage, hasNextPage, errorMessage, isFetching, isLoading, isError } = useGetNotificationsInfinite(statusFilter);
  const { data: notifications, isEmpty } = useInfiniteQueryResult(data);
  const readNotifications = usePostReadNotifications();
  const readAllNotifications = usePostReadAllNotifications();

  const makeNotificationsRead = (data: ReadNotificationsType[]) => {
    readNotifications
      .mutateAsync(data)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [NotificationKey.NOTIFICATIONS_INFINITE],
        });
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  const makeAllVisibleNotificationsRead = () => {
    readNotifications
      .mutateAsync(
        notifications.map((notif) => ({
          id: notif.id,
          type: notif.type,
          entityId: notif.entityId,
        }))
      )
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  const next = () => {
    fetchNextPage();
  };

  const makeAllNotificationsRead = () => {
    readAllNotifications.mutateAsync().catch((error) => {
      toasterMessageHandler(baseErrorMessages, error);
    });
  };

  const onStatusFilterChange = (value: string) => {
    if (isNotificationStatus(value)) {
      setStatusFilter(value);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={'max-w-96'} side={'right'} isClosable={false}>
        <SheetBody>
          <SheetHeader className="border-border-primary border-b">
            <SheetTitle className="mr-0">Powiadomienia</SheetTitle>
            <ToggleGroup type="single" value={statusFilter} onValueChange={onStatusFilterChange} className="ml-auto gap-0.5" variant="default" groupVariant="separate" size="sm" alwaysOneSelected>
              <ToggleGroupItem className="text-xs" value={NotificationStatus.All}>
                Wszystkie
              </ToggleGroupItem>
              <ToggleGroupItem className="text-xs" value={NotificationStatus.Unread}>
                Nieprzeczytane
              </ToggleGroupItem>
            </ToggleGroup>
          </SheetHeader>
          <SheetDescription className={'sr-only'}>Lista powiadomień</SheetDescription>
          <NotificationsList
            notifications={notifications}
            isEmpty={isEmpty}
            isError={isError}
            isLoading={isLoading}
            next={next}
            hasNextPage={hasNextPage}
            makeNotificationsRead={makeNotificationsRead}
            isFetching={isFetching}
            errorMessage={errorMessage}
          />
        </SheetBody>
        <SheetFooter className="border-border-primary dark:bg-card bg-accent border-t">
          <NotificationsSheetActions makeAllVisibleNotificationsRead={makeAllVisibleNotificationsRead} makeAllNotificationsRead={makeAllNotificationsRead} />
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
